// Pictures picked for upload: read in the browser and, when large, scaled down before they are sent.

/** The browser could not decode the file as a picture (not an image, damaged, a format it cannot read). */
export class ImageUnreadable extends Error {}

export interface ScaleOpts {
  /** longest edge of the result (px) */
  maxEdge: number;
  /** a picture within maxEdge, of an accepted type and no larger than this (bytes), is returned as it is */
  keepBelowBytes: number;
  /** WebP / JPEG quality, 0..1 */
  quality: number;
  /** types the receiver accepts as they are */
  accepted: readonly string[];
  /** colour a transparent picture is flattened on */
  background: string;
}

/** The file as it is when it could not be converted: only if the receiver takes its type, else it is unusable here. */
const keep = (file: Blob, o: ScaleOpts) => {
  if (o.accepted.includes(file.type)) return file;
  throw new ImageUnreadable();
};

const toBlob = (c: HTMLCanvasElement, type: string, quality: number) => new Promise<Blob | null>((r) => c.toBlob(r, type, quality));

/**
 * The picture to send for `file`: the file itself when it is already small, else a copy whose longest edge is
 * `maxEdge`, as WebP (JPEG where the browser cannot write WebP, e.g. older Safari). Orientation from the camera
 * (EXIF) is applied by the decoder. A transparent PNG is flattened on white. Throws ImageUnreadable when the file
 * cannot be decoded,
 * or cannot be converted and is not of an accepted type.
 */
export async function scaleImage(file: Blob, o: ScaleOpts): Promise<Blob> {
  let bmp: ImageBitmap;
  try {
    bmp = await createImageBitmap(file);
  } catch {
    throw new ImageUnreadable();
  }
  try {
    const scale = Math.min(1, o.maxEdge / Math.max(bmp.width, bmp.height));
    if (scale === 1 && file.size <= o.keepBelowBytes && o.accepted.includes(file.type)) return file;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bmp.width * scale));
    canvas.height = Math.max(1, Math.round(bmp.height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return keep(file, o);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.fillStyle = o.background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    // a browser that cannot write WebP silently returns PNG: check the type it really wrote
    const webp = await toBlob(canvas, 'image/webp', o.quality);
    const out = webp?.type === 'image/webp' ? webp : await toBlob(canvas, 'image/jpeg', o.quality);
    // never send a bigger file than the one picked, when that one would be accepted as it is
    if (!out) return keep(file, o);
    if (scale === 1 && out.size >= file.size && o.accepted.includes(file.type)) return file;
    return out;
  } finally {
    bmp.close();
  }
}
